/**
 * 患者詳細ページ
 *
 * 患者の基本情報と検査履歴を表示
 * - 患者情報の編集
 * - 新規検査の開始
 * - 検査セッションの詳細表示
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { DeviationChartDataPoint } from "../components/DeviationChart";
import DeviationChart from "../components/DeviationChart";
import { useAuth } from "../contexts/AuthContext";
import { useApi, useMutation } from "../hooks/useApi";
import { api, downloadCsv, downloadZip, ExportFilter } from "../services/api";
import type {
  ExamSessionDetail,
  ExamSessionWithExaminer,
  PatientWithExams,
  TrialResult,
  UpdatePatientRequest,
} from "../types";

function formatJstTimestampForFilename(date: Date): string {
  return new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  })
    .format(date)
    .replace(" ", "_")
    .replace(/:/g, "-");
}

/**
 * 患者情報編集モーダル
 */
interface EditModalProps {
  patient: PatientWithExams;
  onClose: () => void;
  onSave: (data: UpdatePatientRequest) => Promise<void>;
  loading: boolean;
  error: string | null;
}

function EditPatientModal({
  patient,
  onClose,
  onSave,
  loading,
  error,
}: EditModalProps) {
  const [formData, setFormData] = useState<UpdatePatientRequest>({
    patientCode: patient.patientCode,
    name: patient.name,
    nameKana: patient.nameKana || "",
    memo: patient.memo || "",
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">患者情報編集</h2>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                患者コード <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="patientCode"
                value={formData.patientCode}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                氏名
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                フリガナ
              </label>
              <input
                type="text"
                name="nameKana"
                value={formData.nameKana}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                メモ
              </label>
              <textarea
                name="memo"
                value={formData.memo}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}
          </div>
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
              disabled={loading}
            >
              キャンセル
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? "保存中..." : "保存"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * 検査セッション詳細モーダル
 *
 * APIから試行結果を取得し、サマリーと試行ごとの詳細テーブルを表示
 */
interface SessionDetailModalProps {
  session: ExamSessionWithExaminer;
  onClose: () => void;
}

function SessionDetailModal({ session, onClose }: SessionDetailModalProps) {
  const [trialResults, setTrialResults] = useState<TrialResult[]>([]);
  const [deviceInfo, setDeviceInfo] =
    useState<ExamSessionDetail["deviceInfo"]>(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [detailError, setDetailError] = useState<string | null>(null);

  // セッション詳細（試行結果含む）を取得
  const fetchDetail = useCallback(async () => {
    setDetailLoading(true);
    setDetailError(null);
    try {
      const detail = await api.get<ExamSessionDetail>(
        `/api/exams/${session.id}`,
      );
      setTrialResults(detail.trialResults || []);
      setDeviceInfo(detail.deviceInfo ?? null);
    } catch (err) {
      setDetailError(
        err instanceof Error ? err.message : "試行結果の取得に失敗しました",
      );
    } finally {
      setDetailLoading(false);
    }
  }, [session.id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[85vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold text-gray-800">
            検査セッション詳細
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="px-6 py-4 space-y-4">
          {/* 基本情報 */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">検査日時</p>
              <p className="font-medium">
                {new Date(session.createdAt).toLocaleString("ja-JP")}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">検査者</p>
              <p className="font-medium">{session.examiner?.name || "-"}</p>
            </div>
          </div>

          {/* 結果サマリー */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              結果サマリー
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">総試行数</p>
                <p className="text-xl font-bold text-gray-800">
                  {session.totalTrials}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">異常試行数</p>
                <p
                  className={`text-xl font-bold ${session.abnormalTrials > 0 ? "text-red-600" : "text-gray-800"}`}
                >
                  {session.abnormalTrials}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">平均偏差 (mm)</p>
                <p className="text-xl font-bold text-gray-800">
                  {session.averageDeviationMm !== null
                    ? Number(session.averageDeviationMm).toFixed(2)
                    : "-"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">平均偏差 (%)</p>
                <p className="text-xl font-bold text-gray-800">
                  {session.averageDeviationPercent !== null
                    ? `${Number(session.averageDeviationPercent).toFixed(2)}%`
                    : "-"}
                </p>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-gray-500">総合判定</p>
                <p
                  className={`text-xl font-bold ${
                    session.settings?.disableAbnormalJudgment
                      ? "text-gray-700"
                      : session.abnormalTrials > 0
                        ? "text-red-600"
                        : "text-green-600"
                  }`}
                >
                  {session.settings?.disableAbnormalJudgment
                    ? "判定無し"
                    : session.abnormalTrials > 0
                      ? "要注意"
                      : "正常"}
                </p>
              </div>
            </div>
          </div>

          {/* 端末情報 */}
          {deviceInfo && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                端末情報
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-500">画面解像度:</span>
                  <span className="ml-2 font-medium">
                    {deviceInfo.screenWidth}x{deviceInfo.screenHeight}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">ビューポート:</span>
                  <span className="ml-2 font-medium">
                    {deviceInfo.viewportWidth}x{deviceInfo.viewportHeight}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">デバイスピクセル比:</span>
                  <span className="ml-2 font-medium">
                    {deviceInfo.devicePixelRatio}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">プラットフォーム:</span>
                  <span className="ml-2 font-medium">
                    {deviceInfo.platform || "-"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Monitor count:</span>
                  <span className="ml-2 font-medium">
                    {deviceInfo.monitorCount ??
                      deviceInfo.monitors?.length ??
                      "-"}
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500">User-Agent:</span>
                  <span className="ml-2 font-medium text-xs break-all">
                    {deviceInfo.userAgent || "-"}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* 試行詳細テーブル */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              試行詳細
            </h3>
            {detailLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                <span className="ml-2 text-sm text-gray-500">
                  読み込み中...
                </span>
              </div>
            ) : detailError ? (
              <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
                <p className="text-red-600 text-sm">{detailError}</p>
                <button
                  onClick={fetchDetail}
                  className="ml-2 underline hover:no-underline"
                >
                  再試行
                </button>
              </div>
            ) : trialResults.length === 0 ? (
              <p className="text-sm text-gray-500 py-2">試行結果がありません</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                        試行
                      </th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                        線の長さ (mm)
                      </th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                        偏差 (mm)
                      </th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                        偏差 (%)
                      </th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                        判定
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {trialResults.map((trial) => (
                      <tr
                        key={trial.id}
                        className={trial.isAbnormal ? "bg-red-50" : "bg-white"}
                      >
                        <td className="px-3 py-2 text-center text-gray-900">
                          {trial.trialNumber}
                        </td>
                        <td className="px-3 py-2 text-center text-gray-900">
                          {Number(trial.lineLengthMm).toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-center text-gray-900">
                          {Number(trial.deviationMm).toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-center text-gray-900">
                          {Number(trial.deviationPercent).toFixed(2)}%
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              trial.isAbnormal
                                ? "bg-red-100 text-red-800"
                                : "bg-green-100 text-green-800"
                            }`}
                          >
                            {trial.isAbnormal ? "異常" : "正常"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* 検査設定 */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              検査設定
            </h3>
            <div className="text-sm text-gray-600 space-y-1">
              <p>試行回数: {session.settings.trialCount}回</p>
              <p>線分長: {session.settings.lineLengths.join(", ")} mm</p>
              <p>線分の太さ: {session.settings.lineThickness} mm</p>
              <p>異常判定閾値: {session.settings.abnormalThreshold}%</p>
              <p>入力モード: {session.settings.inputMode}</p>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 患者詳細ページ
 */
function PatientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  // 施設管理者以上かどうか（一括エクスポートの表示制御用）
  const isFacilityAdminOrAbove =
    user?.role === "SYSTEM_ADMIN" || user?.role === "FACILITY_ADMIN";

  // 状態管理
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSession, setSelectedSession] =
    useState<ExamSessionWithExaminer | null>(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filteredSessions, setFilteredSessions] = useState<
    ExamSessionWithExaminer[]
  >([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterLoading, setFilterLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo] = useState("");
  const [exportLineLengths, setExportLineLengths] = useState<number[]>([]);
  const sessionsPerPage = 10;

  // API呼び出し
  const {
    data: patient,
    loading,
    error,
    refetch,
  } = useApi<PatientWithExams>(`/api/patients/${id}`, [id]);

  // 患者更新用のミューテーション
  const updateMutation = useMutation<PatientWithExams, UpdatePatientRequest>(
    `/api/patients/${id}`,
    "PUT",
  );

  // 日付フィルタが変更された場合、APIから再取得
  const fetchFilteredSessions = useCallback(async () => {
    if (!id) return;
    if (!dateFrom && !dateTo) {
      // フィルタなしの場合はpatientデータのセッションを使用
      setFilteredSessions(patient?.examSessions || []);
      return;
    }
    setFilterLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("from", dateFrom);
      if (dateTo) params.set("to", dateTo);
      const sessions = await api.get<ExamSessionWithExaminer[]>(
        `/api/exams/patient/${id}?${params.toString()}`,
      );
      setFilteredSessions(sessions);
    } catch {
      // フィルタエラー時はpatientデータのセッションにフォールバック
      setFilteredSessions(patient?.examSessions || []);
    } finally {
      setFilterLoading(false);
    }
  }, [id, dateFrom, dateTo, patient?.examSessions]);

  // patientデータの読み込み完了時、またはフィルタ変更時にセッション一覧を更新
  useEffect(() => {
    fetchFilteredSessions();
  }, [fetchFilteredSessions]);

  // フィルタリセット
  const handleResetFilter = () => {
    setDateFrom("");
    setDateTo("");
    setCurrentPage(1);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [dateFrom, dateTo]);

  // 患者情報更新ハンドラ
  const handleSavePatient = async (data: UpdatePatientRequest) => {
    const result = await updateMutation.mutate(data);
    if (result) {
      setShowEditModal(false);
      refetch();
    }
  };

  // 経過グラフ用データの生成
  const chartData: DeviationChartDataPoint[] = useMemo(() => {
    if (!filteredSessions || filteredSessions.length < 2) return [];
    return filteredSessions
      .filter(
        (s) =>
          s.averageDeviationMm !== null && s.averageDeviationPercent !== null,
      )
      .map((s) => ({
        date: s.createdAt,
        averageDeviationMm: Number(s.averageDeviationMm),
        averageDeviationPercent: Number(s.averageDeviationPercent),
        abnormalTrials: s.abnormalTrials,
        totalTrials: s.totalTrials,
      }));
  }, [filteredSessions]);

  // 検査設定から異常閾値を取得（最新セッションの設定を使用）
  const abnormalThreshold = useMemo(() => {
    if (!filteredSessions || filteredSessions.length === 0) return undefined;
    return filteredSessions[0]?.settings?.abnormalThreshold;
  }, [filteredSessions]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredSessions.length / sessionsPerPage),
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedSessions = useMemo(() => {
    const start = (currentPage - 1) * sessionsPerPage;
    return filteredSessions.slice(start, start + sessionsPerPage);
  }, [currentPage, filteredSessions]);

  const startItemIndex =
    filteredSessions.length === 0 ? 0 : (currentPage - 1) * sessionsPerPage + 1;
  const endItemIndex = Math.min(
    currentPage * sessionsPerPage,
    filteredSessions.length,
  );

  // 新規検査開始
  const handleStartExam = () => {
    navigate(`/exam/settings?patientId=${id}`);
  };

  // 検査データから使用されている線の長さ一覧を取得
  const availableLineLengths = useMemo(() => {
    if (!patient?.examSessions) return [];
    const lengths = new Set<number>();
    for (const session of patient.examSessions) {
      const settings = session.settings as { lineLengths?: number[] };
      if (settings?.lineLengths) {
        for (const len of settings.lineLengths) lengths.add(len);
      }
    }
    return [...lengths].sort((a, b) => a - b);
  }, [patient]);

  // 一括ZIPエクスポート（フィルタ付き）
  const handleExportZip = async () => {
    if (!id || !patient) return;
    setExportLoading(true);
    try {
      const filter: ExportFilter = {};
      if (exportFrom) filter.from = exportFrom;
      if (exportTo) filter.to = exportTo;
      if (exportLineLengths.length > 0) filter.lineLengths = exportLineLengths;
      const timestampStr = formatJstTimestampForFilename(new Date());
      const filename = `export_${patient.patientCode}_${timestampStr}.zip`;
      await downloadZip(id, filename, filter);
      setShowExportDialog(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : "エクスポートに失敗しました");
    } finally {
      setExportLoading(false);
    }
  };

  // ローディング表示
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  // エラー表示
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h2 className="text-red-800 font-semibold mb-2">
          エラーが発生しました
        </h2>
        <p className="text-red-600 mb-4">{error}</p>
        <div className="flex space-x-2">
          <button
            onClick={refetch}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            再試行
          </button>
          <Link
            to="/patients"
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            患者一覧に戻る
          </Link>
        </div>
      </div>
    );
  }

  // 患者が見つからない場合
  if (!patient) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <h2 className="text-yellow-800 font-semibold mb-2">
          患者が見つかりません
        </h2>
        <p className="text-yellow-600 mb-4">
          指定された患者は存在しないか、削除されています。
        </p>
        <Link
          to="/patients"
          className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
        >
          患者一覧に戻る
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* ページヘッダー */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <Link to="/patients" className="text-gray-500 hover:text-gray-700">
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </Link>
          <h1 className="text-2xl font-bold text-gray-800">患者詳細</h1>
        </div>
        <div className="flex space-x-2">
          {isFacilityAdminOrAbove && filteredSessions.length > 0 && (
            <button
              onClick={() => setShowExportDialog(true)}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
            >
              一括エクスポート
            </button>
          )}
          <button
            onClick={() => setShowEditModal(true)}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
          >
            編集
          </button>
          <button
            onClick={handleStartExam}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            新規検査
          </button>
        </div>
      </div>

      {/* 基本情報カード */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">基本情報</h2>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <dt className="text-sm text-gray-500">患者コード</dt>
            <dd className="mt-1 text-lg font-medium text-gray-900">
              {patient.patientCode}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-gray-500">氏名</dt>
            <dd className="mt-1 text-lg font-medium text-gray-900">
              {patient.name}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-gray-500">フリガナ</dt>
            <dd className="mt-1 text-lg text-gray-900">
              {patient.nameKana || "-"}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-gray-500">登録日</dt>
            <dd className="mt-1 text-lg text-gray-900">
              {new Date(patient.createdAt).toLocaleDateString("ja-JP")}
            </dd>
          </div>
          {patient.memo && (
            <div className="md:col-span-2">
              <dt className="text-sm text-gray-500">メモ</dt>
              <dd className="mt-1 text-gray-900 whitespace-pre-wrap">
                {patient.memo}
              </dd>
            </div>
          )}
        </dl>
      </div>

      {/* 経過グラフ（検査が2件以上ある場合のみ表示） */}
      {chartData.length >= 2 && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            経過グラフ
          </h2>
          <DeviationChart data={chartData} threshold={abnormalThreshold} />
        </div>
      )}

      {/* 検査履歴 */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">検査履歴</h2>
        </div>

        {/* 日付フィルタ */}
        <div className="px-6 py-3 border-b border-gray-100 bg-gray-50">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                開始日
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="px-3 py-1.5 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                終了日
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="px-3 py-1.5 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            {(dateFrom || dateTo) && (
              <button
                onClick={handleResetFilter}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border rounded hover:bg-gray-100"
              >
                リセット
              </button>
            )}
            {filterLoading && (
              <div className="flex items-center text-sm text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-1"></div>
                フィルタ中...
              </div>
            )}
          </div>
        </div>

        {filteredSessions.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <p className="mb-4">
              {dateFrom || dateTo
                ? "該当する検査履歴がありません"
                : "検査履歴がありません"}
            </p>
            {!dateFrom && !dateTo && (
              <button
                onClick={handleStartExam}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                検査を開始する
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    検査日時
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    検査者
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    試行数
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    平均偏差 (mm)
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    平均偏差 (%)
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    異常試行
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    総合判定
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    端末
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    CSV
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    詳細
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedSessions.map((session) => (
                  <tr key={session.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(session.createdAt).toLocaleString("ja-JP")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {session.examiner?.name || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {session.totalTrials}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {session.averageDeviationMm !== null
                        ? Number(session.averageDeviationMm).toFixed(2)
                        : "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {session.averageDeviationPercent !== null
                        ? `${Number(session.averageDeviationPercent).toFixed(2)}%`
                        : "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          session.abnormalTrials > 0
                            ? "bg-red-100 text-red-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {session.abnormalTrials}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          session.settings?.disableAbnormalJudgment
                            ? "bg-gray-100 text-gray-700"
                            : session.abnormalTrials > 0
                              ? "bg-red-100 text-red-800"
                              : "bg-green-100 text-green-800"
                        }`}
                      >
                        {session.settings?.disableAbnormalJudgment
                          ? "判定無し"
                          : session.abnormalTrials > 0
                            ? "要注意"
                            : "正常"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-xs text-gray-500">
                      {session.deviceInfo ? (
                        <>
                          <div>{session.deviceInfo.platform || "-"}</div>
                          <div>
                            {session.deviceInfo.screenWidth}x
                            {session.deviceInfo.screenHeight}
                          </div>
                          <div>
                            Monitors:{" "}
                            {session.deviceInfo.monitorCount ??
                              session.deviceInfo.monitors?.length ??
                              "-"}
                          </div>
                        </>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                      <button
                        onClick={() => {
                          const timestampStr = formatJstTimestampForFilename(
                            new Date(session.createdAt),
                          );
                          downloadCsv(
                            session.id,
                            `exam_${patient.patientCode}_${timestampStr}.csv`,
                          );
                        }}
                        className="text-green-600 hover:text-green-800"
                        title="CSVダウンロード"
                      >
                        <svg
                          className="w-5 h-5 inline"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                      <button
                        onClick={() => setSelectedSession(session)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        表示
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* 件数表示 */}
        {filteredSessions.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-sm text-gray-500">
                {dateFrom || dateTo
                  ? `${filteredSessions.length} 件 (フィルタ適用中)`
                  : `全 ${filteredSessions.length} 件`}
                {` / ${startItemIndex}-${endItemIndex} 件表示`}
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  前へ
                </button>
                <span className="text-sm text-gray-600 min-w-[84px] text-center">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  次へ
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 編集モーダル */}
      {showEditModal && (
        <EditPatientModal
          patient={patient}
          onClose={() => setShowEditModal(false)}
          onSave={handleSavePatient}
          loading={updateMutation.loading}
          error={updateMutation.error}
        />
      )}

      {/* 検査セッション詳細モーダル */}
      {selectedSession && (
        <SessionDetailModal
          session={selectedSession}
          onClose={() => setSelectedSession(null)}
        />
      )}

      {/* エクスポート条件指定ダイアログ */}
      {showExportDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              一括エクスポート
            </h3>
            <div className="space-y-4">
              {/* 期間指定 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  期間
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="date"
                    value={exportFrom}
                    onChange={(e) => setExportFrom(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-gray-400">〜</span>
                  <input
                    type="date"
                    value={exportTo}
                    onChange={(e) => setExportTo(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-400">未指定で全期間</p>
              </div>

              {/* 線の長さフィルタ */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  線の長さ
                </label>
                <div className="flex flex-wrap gap-2">
                  {availableLineLengths.map((len) => (
                    <button
                      key={len}
                      onClick={() =>
                        setExportLineLengths((prev) =>
                          prev.includes(len)
                            ? prev.filter((l) => l !== len)
                            : [...prev, len].sort((a, b) => a - b),
                        )
                      }
                      className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                        exportLineLengths.includes(len)
                          ? "bg-green-500 text-white border-green-500"
                          : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                      }`}
                    >
                      {len}mm
                    </button>
                  ))}
                </div>
                <p className="mt-1 text-xs text-gray-400">
                  {exportLineLengths.length === 0
                    ? "未選択で全長さ"
                    : `選択中: ${exportLineLengths.join(", ")}mm`}
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-between items-center">
              <button
                onClick={() => {
                  setExportFrom("");
                  setExportTo("");
                  setExportLineLengths([]);
                }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                条件をリセット
              </button>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowExportDialog(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleExportZip}
                  disabled={exportLoading}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  {exportLoading ? "エクスポート中..." : "エクスポート"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PatientDetailPage;
